import React, { Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import ChromeArtifact from "./ChromeArtifact";

const HERO_BG =
  "https://images.unsplash.com/photo-1556624651-1f527cdf6508?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxzdWJ3YXklMjB0cmFpbiUyMG5pZ2h0JTIwYmx1cnxlbnwwfHx8fDE3ODQzMTI4ODl8MA&ixlib=rb-4.1.0&q=85";

const words = ["LEAVE", "AN", "ECHO"];

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden bg-black"
    >
      {/* Cinematic moving background */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.15 }}
        animate={{ scale: 1.0 }}
        transition={{ duration: 6, ease: "easeOut" }}
      >
        <video
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover saturate-[0.35] contrast-[1.05]"
          style={{ filter: "blur(2px)" }}
          poster={HERO_BG}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source
            src={`${process.env.PUBLIC_URL}/hero-subway.mp4`}
            type="video/mp4"
          />
        </video>
      </motion.div>

      {/* Tunnel light streaks (train motion) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute h-[1px] w-[40vw] bg-gradient-to-r from-transparent via-white/40 to-transparent"
            style={{ top: `${28 + i * 20}%` }}
            initial={{ x: "-50vw" }}
            animate={{ x: "150vw" }}
            transition={{
              duration: 3.2 + i * 0.6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1.1,
            }}
          />
        ))}
      </div>

      {/* Scrim */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

      {/* 3D chrome artifact */}
      <div className="absolute inset-0 z-10">
        <Suspense fallback={null}>
          <ChromeArtifact />
        </Suspense>
      </div>

      {/* Overlay content */}
      <div className="relative z-20 h-full flex flex-col justify-center px-6 md:px-12 pointer-events-none">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="font-mono-tech text-xs uppercase tracking-[0.4em] text-steel-light mb-6"
        >
          FW / 01 — Underground
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.85] text-white text-[18vw] md:text-[13vw]">
          {words.map((w, i) => (
            <span key={w} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 1,
                  delay: 0.5 + i * 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {w === "ECHO" ? <span className="chrome-text">{w}</span> : w}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="max-w-md mt-8 text-steel-light text-sm md:text-base leading-relaxed"
        >
          Premium editorial streetwear engineered for the neon dark. Worn on the
          last train home — and remembered long after the doors close.
        </motion.p>
      </div>

      {/* Bottom metadata */}
      <div className="absolute bottom-8 left-6 md:left-12 z-20 flex items-center gap-3 text-steel">
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="font-mono-tech text-[10px] uppercase tracking-widest">
          Scroll to enter
        </span>
      </div>
      <div className="absolute bottom-8 right-6 md:right-12 z-20 text-right">
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-steel">
          51.5074° N / 0.1278° W
        </span>
      </div>
    </section>
  );
}
